import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { QuestionForm } from './Form/QuestionForm'
import { themeVariables } from '../themes/themeVariables'
import { Button } from './Button'
import { useCreateAnswer } from '../utils/mutations'
import { Icon } from './Icon'
import { useTeamPermissionsForResourceQuery } from '../utils/queries'
import { useRunAnswerWithFaultyDocumentsHandling } from '../utils/assessments'

const AnswerContainer = styled.div`
    padding: 24px;
    background-color: ${themeVariables.colors.backgroundContainer};
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const FormContainer = styled.div`
    background-color: ${themeVariables.colors.backgroundSurface};
    border-radius: 4px;
`

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`

const Spinner = styled(Icon)`
    color: ${themeVariables.colors.backgroundSurface};
    width: 14px;
    height: 14px;
    svg {
        width: 14px;
        height: 14px;
    }
`

const SpinnerContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

export const NewQuestion = ({ assessmentId }: { assessmentId: string }) => {
    const [question, setQuestion] = useState('')

    const createAnswer = useCreateAnswer(assessmentId)
    const { handleRunAnswer, isPending: runAnswerIsPending } =
        useRunAnswerWithFaultyDocumentsHandling()

    const { data: permission, isFetching: teamPermissionIsLoading } =
        useTeamPermissionsForResourceQuery({
            assessmentId,
        })

    const handleCreateAnswer = useCallback(async () => {
        try {
            const answer = await createAnswer.mutateAsync({ question })
            setQuestion('')
            await handleRunAnswer(answer)
        } catch (error) {
            console.error('Failed to create and run answer', error)
        }
    }, [question, createAnswer, handleRunAnswer])

    return (
        <AnswerContainer>
            <p className="m-b-0 large emphasized">Add a new question</p>
            <FormContainer>
                <QuestionForm
                    disabled={
                        teamPermissionIsLoading ||
                        permission === 'Viewer' ||
                        createAnswer.isPending ||
                        runAnswerIsPending
                    }
                    value={{ question }}
                    onChange={({ question }) => setQuestion(question)}
                    displayCategorySelection={false}
                />
            </FormContainer>
            <ButtonContainer>
                <Button
                    disabled={
                        !question ||
                        teamPermissionIsLoading ||
                        permission === 'Viewer' ||
                        createAnswer.isPending ||
                        runAnswerIsPending
                    }
                    onClick={handleCreateAnswer}
                >
                    {createAnswer.isPending || runAnswerIsPending ? (
                        <SpinnerContainer>
                            Saving
                            <Spinner name="spinner" />
                        </SpinnerContainer>
                    ) : (
                        'Save and run question'
                    )}
                </Button>
            </ButtonContainer>
        </AnswerContainer>
    )
}
